<template>
    <div class="credentials-display">
        <div class="success-header">
            <div class="success-icon">
                <div class="checkmark-wrapper">
                    <svg class="checkmark" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 52 52">
                        <circle class="checkmark__circle" cx="26" cy="26" r="25" fill="none" />
                        <path class="checkmark__check" fill="none" d="M14.1 27.2l7.1 7.2 16.7-16.8" />
                    </svg>
                </div>
            </div>
            <h4>Connection Created Successfully!</h4>
            <p class="text-muted">Save these credentials securely - the access token will only be shown once</p>
        </div>

        <!-- Warning Alert -->
        <div class="alert alert-warning">
            <i class="fas fa-exclamation-triangle"></i>
            <div>
                <strong>Important:</strong> Make sure to copy and save the access token now.
                You won't be able to see it again after leaving this page.
            </div>
        </div>

        <!-- Credentials Cards -->
        <div class="credentials-grid">
            <!-- Access Token -->
            <div class="credential-card">
                <div class="credential-header">
                    <i class="fas fa-key"></i>
                    <span>Personal Access Token</span>
                </div>
                <div class="credential-body">
                    <div class="credential-value-wrapper">
                        <input :type="showToken ? 'text' : 'password'" :value="credentials.access_token"
                            class="credential-value" readonly />
                        <button @click="toggleTokenVisibility" class="btn-toggle" :title="showToken ? 'Hide' : 'Show'">
                            <i :class="showToken ? 'fas fa-eye-slash' : 'fas fa-eye'"></i>
                        </button>
                        <button @click="copyToClipboard('access_token')" class="btn-copy" title="Copy to clipboard">
                            <i class="fas fa-copy"></i>
                        </button>
                    </div>
                </div>
            </div>

            <!-- Product ID -->
            <div class="credential-card">
                <div class="credential-header">
                    <i class="fas fa-bed"></i>
                    <span>Product ID</span>
                </div>
                <div class="credential-body">
                    <div class="credential-value-wrapper">
                        <input type="text" :value="credentials.product_id"
                            class="credential-value" readonly />
                        <button @click="copyToClipboard('product_id')" class="btn-copy" title="Copy to clipboard">
                            <i class="fas fa-copy"></i>
                        </button>
                    </div>
                </div>
            </div>

            <!-- X-Tenant-Domain -->
            <div class="credential-card">
                <div class="credential-header">
                    <i class="fas fa-globe"></i>
                    <span>X-Tenant-Domain</span>
                </div>
                <div class="credential-body">
                    <div class="credential-value-wrapper">
                        <input type="text" :value="tenantDomain" class="credential-value" readonly />
                        <button @click="copyToClipboard('tenant_domain')" class="btn-copy" title="Copy to clipboard">
                            <i class="fas fa-copy"></i>
                        </button>
                    </div>
                </div>
            </div>

            <!-- Auth URL -->
            <div class="credential-card">
                <div class="credential-header">
                    <i class="fas fa-link"></i>
                    <span>Auth URL</span>
                </div>
                <div class="credential-body">
                    <div class="credential-value-wrapper">
                        <input type="text" :value="credentials.auth_url" class="credential-value" readonly />
                        <button @click="copyToClipboard('auth_url')" class="btn-copy" title="Copy to clipboard">
                            <i class="fas fa-copy"></i>
                        </button>
                    </div>
                </div>
            </div>
        </div>

        <!-- Integration Guide -->
        <div class="integration-guide">
            <h5><i class="fas fa-book"></i> Integration Guide</h5>
            <p>Use these credentials to integrate with other System:</p>
            <ol>
                <li>Include the <strong>Personal Access Token</strong> for Authorization</li>
                <li>Set the <strong>X-Tenant-Domain</strong> for multi-tenancy</li>
                <li>Use <strong>Product ID</strong> to connect the property product on other System</li>
                <li>Check the <strong>Auth URL</strong> to see if it matches the property ID on other System</li>
            </ol>
        </div>
    </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue'
import { useToast } from '@/composables/useToast'

export default {
    name: 'CredentialsDisplay',
    props: {
        credentials: {
            type: Object,
            required: true,
            default: () => ({
                access_token: '',
                property_id: '',
                product_id: '',
                x_tenant_domain: '',
                auth_url: ''
            })
        }
    },
    setup(props) {
        const showToken = ref(false)
        const { showToast } = useToast()

        /**
         * Computed property for the tenant domain
         * Returns x_tenant_domain from credentials or falls back to window.location.hostname
         * 
         * @returns {string} Tenant domain
         */
        const tenantDomain = computed(() => {
            return props.credentials.x_tenant_domain || window.location.hostname
        })

        onMounted(() => {
            window.scrollTo(0, 0)
            document.documentElement.scrollTop = 0
            document.body.scrollTop = 0
            setTimeout(() => {
                window.scrollTo(0, 0)
                document.documentElement.scrollTop = 0
                document.body.scrollTop = 0

                const appContainer = document.querySelector('.app-content') || document.querySelector('.main-content')
                if (appContainer) {
                    appContainer.scrollTop = 0
                }
            }, 100)
        })

        /**
         * Toggle visibility of the access token
         */
        function toggleTokenVisibility() {
            showToken.value = !showToken.value
        }

        /**
         * Copy the specified field value to the clipboard
         * Shows a toast notification on success or error
         * 
         * @param {string} field Field identifier to copy ('access_token', 'auth_url', 'product_id', 'tenant_domain')
         */
        async function copyToClipboard(field) {
            let value = ''
            let label = ''

            switch (field) {
                case 'access_token':
                    value = props.credentials.access_token
                    label = 'Access Token'
                    break
                case 'auth_url':
                    value = props.credentials.auth_url
                    label = 'Auth URL'
                    break
                case 'product_id':
                    value = props.credentials.product_id
                    label = 'Product ID'
                    break
                case 'tenant_domain':
                    value = tenantDomain.value
                    label = 'X-Tenant-Domain'
                    break
            }

            try {
                await navigator.clipboard.writeText(value)
                showToast({
                    type: 'success',
                    title: 'Copied!',
                    message: `${label} copied to clipboard!`
                })
            } catch (err) {
                console.error('Failed to copy:', err)
                showToast({
                    type: 'error',
                    title: 'Copy Failed',
                    message: 'Failed to copy to clipboard'
                })
            }
        }

        return {
            showToken,
            tenantDomain,
            toggleTokenVisibility,
            copyToClipboard
        }
    }
}
</script>

<style scoped>
.credentials-display {
    padding: 1.5rem;
}

.success-header {
    text-align: center;
    margin-bottom: 2rem;
}

.success-icon {
    width: 64px;
    height: 64px;
    margin: 0 auto 1rem;
    background: color-mix(in srgb, var(--primary-color), transparent 90%);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
}

.success-icon i {
    font-size: 2rem;
    color: var(--primary-color, #2ecc71);
}

.checkmark-wrapper {
    width: 80px;
    height: 80px;
}

.checkmark {
    width: 80px;
    height: 80px;
    border-radius: 50%;
    display: block;
    stroke-width: 3;
    stroke: #fff;
    stroke-miterlimit: 10;
    box-shadow: inset 0px 0px 0px var(--primary-color, #2ecc71);
    animation: fill .4s ease-in-out .4s forwards, scale .3s ease-in-out .9s both;
}

.checkmark__circle {
    stroke-dasharray: 166;
    stroke-dashoffset: 166;
    stroke-width: 2;
    stroke-miterlimit: 10;
    stroke: var(--primary-color, #2ecc71);
    fill: none;
    animation: stroke 0.6s cubic-bezier(0.65, 0, 0.45, 1) forwards;
}

.checkmark__check {
    transform-origin: 50% 50%;
    stroke-dasharray: 48;
    stroke-dashoffset: 48;
    animation: stroke 0.3s cubic-bezier(0.65, 0, 0.45, 1) 0.8s forwards;
}

@keyframes stroke {
    100% {
        stroke-dashoffset: 0;
    }
}

@keyframes scale {
    0%, 100% {
        transform: none;
    }
    50% {
        transform: scale3d(1.1, 1.1, 1);
    }
}

@keyframes fill {
    100% {
        box-shadow: inset 0px 0px 0px 50px var(--primary-color, #2ecc71);
    }
}
.success-header h4 {
    margin-bottom: 0.5rem;
    color: #111827;
}

.text-muted {
    color: #6b7280;
    font-size: 0.875rem;
}

.alert {
    padding: 1rem;
    border-radius: 8px;
    font-size: 0.875rem;
    margin-bottom: 2rem;
}

.alert-warning {
    background: #fef3c7;
    color: #92400e;
    border: 1px solid #fde68a;
    display: flex;
    gap: 0.75rem;
}

.alert-warning i {
    color: #f59e0b;
    margin-top: 0.125rem;
}

.credentials-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 1.5rem;
    margin-bottom: 2rem;
}

@media (max-width: 768px) {
    .credentials-grid {
        grid-template-columns: 1fr;
    }
}

.credential-card {
    background: white;
    border: 1px solid #e5e7eb;
    border-radius: 12px;
    overflow: hidden;
}

.credential-header {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.875rem 1.25rem;
    background: #f9fafb;
    border-bottom: 1px solid #e5e7eb;
    font-weight: 600;
    color: #374151;
    font-size: 0.875rem;
}

.credential-header i {
    color: var(--primary-color, #F93E3E);
}

.credential-body {
    padding: 1rem 1.25rem;
}

.credential-value-wrapper {
    display: flex;
    gap: 0.5rem;
}

.credential-value {
    flex: 1;
    padding: 0.625rem 0.875rem;
    border: 1px solid #d1d5db;
    border-radius: 8px;
    font-family: 'Courier New', monospace;
    font-size: 0.875rem;
    background: #f9fafb;
    color: #111827;
}

.btn-toggle,
.btn-copy {
    padding: 0.625rem 0.875rem;
    border: 1px solid #d1d5db;
    border-radius: 8px;
    background: white;
    color: #6b7280;
    cursor: pointer;
    transition: all 0.2s ease;
}

.btn-toggle:hover,
.btn-copy:hover {
    background: #f3f4f6;
    border-color: #9ca3af;
    color: #374151;
}



.integration-guide {
    background: color-mix(in srgb, var(--primary-color), transparent 95%);
    border: 1px solid color-mix(in srgb, var(--primary-color), transparent 85%);
    border-radius: 12px;
    padding: 1.5rem;
}

.integration-guide h5 {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-bottom: 1rem;
    color: var(--primary-color, #F93E3E);
}

.integration-guide h5 i {
    color: var(--primary-color, #F93E3E);
}

.integration-guide p {
    color: #374151;
    margin-bottom: 0.75rem;
}

.integration-guide ol {
    margin: 0;
    padding-left: 1.5rem;
    color: #374151;
}

.integration-guide li {
    margin-bottom: 0.5rem;
}

.integration-guide strong {
    color: var(--primary-color, #F93E3E);
}
</style>
